<template>
  <a-table
    :bordered="true"
    size="small"
    :loading="state.loading"
    :scroll="{ y: 400, x: columnState.tableWidth }"
    :row-selection="rowSelection"
    :columns="dynamicColumns"
    row-key="id"
    :data-source="state.dataSource"
    :pagination="{
      current: state.current,
      pageSize: state.pageSize,
      total: state.total,
    }"
    @change="handleTableChange"
  >
    <template
      v-for="item in slotList"
      v-slot:[item.slotName]="{ index, text }"
      :key="item.slotName"
    >
      <span v-if="item.slotName === 'index'">
        {{ index + 1 + state.pageSize * (state.current - 1) }}
      </span>
      <span v-if="item.type === 'custom'">
        {{ item['field_' + text] }}
      </span>
      <span v-if="item.type === 'datetime'">
        {{ getDateTime(text) }}
      </span>
      <span v-if="item.type === 'date'">
        {{ getDate(text) }}
      </span>
      <span v-if="item.type === 'full_date'">
        {{ getFullDate(text) }}
      </span>
      <span v-if="item.type === 'time'">
        {{ getTime(text) }}
      </span>
      <a-tag
        color="green"
        v-if="text && item.type === 'boolean'"
      >
        {{ item.name1 }}
      </a-tag>
      <a-tag
        color="red"
        v-if="!text && item.type === 'boolean'"
      >
        {{ item.name2 }}
      </a-tag>
      <template v-if="item.type === 'list'">
        <a-tag
          color="#87d068"
          v-for="obj in text"
          :key="obj.id"
          style="margin-bottom: 1px"
        >
          {{ obj.name }}
        </a-tag>
      </template>
    </template>
  </a-table>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref, watch } from 'vue';
// import { ColumnProps } from 'ant-design-vue/es/table/interface';
import { useFetchData } from 'liyu-pc-base/utils/hooks/useFetchData';
import { useForm } from 'ant-design-vue/es/form';

import getFormConfig from 'liyu-pc-base/components/FormRender/RemoteModal/index';
import { useTableDynamicColumns } from 'liyu-pc-base/utils/hooks/useTableColumn';
import { getDate, getDateTime, getFullDate, getTime } from 'liyu-pc-base/utils/function';

// type Key = ColumnProps['key'];

// interface DataType {
//   id: number;
//   key: Key;
//   name: string;
// }

export default defineComponent({
  name: 'RemoteTable',
  props: {
    field: {
      type: Object,
      required: true,
    },
    modalType: {
      type: String,
      required: true,
      default: () => '',
    },
    value: {
      type: Array,
      required: true,
      default: () => [],
    },
    init_model: {
      type: Object,
      default: () => {},
    },
    field_status: {
      type: Object,
      default: () => {},
    },
    watch: {
      type: Array,
      default: () => [],
    },
    formModel: {
      type: Object,
      required: true,
    },
  },
  emits: ['update:value'],
  setup(props, { emit }) {
    const loading = ref(false);
    const { searchConfig, searchConfigOptions, getData, columns, slotList } = getFormConfig(
      props.modalType,
    );
    const search_modelRef = reactive({
      ...searchConfig.model,
      is_active: true,
      ...props.init_model,
    });

    for (const field of searchConfig.fields) {
      if (props.field_status && props.field_status.hasOwnProperty(field.name)) {
        if (props.field_status[field.name] == 0) {
          field.disabled = true;
          field.hidden = false;
        } else if (props.field_status[field.name] == 1) {
          field.disabled = false;
          field.hidden = true;
        } else {
          field.disabled = false;
          field.hidden = false;
        }
      }
    }

    const old_obj: { [key: string]: any } = {};
    onMounted(() => {
      if (props.watch) {
        for (const k of props.watch) {
          if (props.formModel) {
            old_obj[k as string] = props.formModel[k as string];
          }
        }
      }
      if (props.watch && props.formModel) {
        for (const k of props.watch) {
          search_modelRef[k as string] = props.formModel[k as string];
          watch(
            () => props.formModel[k as string],
            () => {
              if (props.watch && props.formModel) {
                for (const k of props.watch) {
                  if (old_obj[k as string] != props.formModel[k as string]) {
                    search_modelRef[k as string] = props.formModel[k as string];
                  }
                }
              }
            },
            { immediate: true },
          );
        }
      }
    });

    const { state: columnState, dynamicColumns } = useTableDynamicColumns(columns, {
      checkAll: false,
      needRowIndex: false,
    });
    const datasource = computed(() => {
      return modalRef.datasource.map(item => {
        return {
          value: item[props.field.valueKey ? (props.field.valueKey as string) : 'id'],
          label: item[props.field.labelKey ? (props.field.labelKey as string) : 'name'],
        };
      });
    });
    const modalRef = reactive({
      visible: false,
      selectedRows: new Array<number>(),
      selectedRowsKey: new Array<number>(),
      selectedRowsTotal: new Array<any>(),
      datasource: new Array<any>(),
    });

    const rowSelection = {
      type:
        props.field.mode == 'default'
          ? 'radio'
          : props.field.mode == 'multiple'
          ? 'checkbox'
          : 'checkbox',
      columnWidth: '30px',
      selectedRowKeys: modalRef.selectedRowsKey,
      onChange: (selectedRowKeys: any, selectedRows: any) => {
        // modalRef.selectedRowsKey.length = 0;
        const remove_key: Array<number> = modalRef.selectedRowsKey.filter(
          item => selectedRowKeys.indexOf(Number(item)) == -1,
        );
        modalRef.selectedRowsTotal = modalRef.selectedRowsTotal.filter(
          item => remove_key.indexOf(item.id) == -1,
        );
        selectedRows.forEach((item: any) => {
          if (modalRef.selectedRowsKey.indexOf(item.id) == -1) {
            modalRef.selectedRowsTotal.push(item);
          }
        });

        modalRef.selectedRowsKey.length = 0;
        selectedRows.forEach((item: any) => {
          modalRef.selectedRowsKey.push(item.id);
          modalRef.datasource.push(item);
        });
      },
    };

    const {
      reload,
      setPageInfo,
      context: state,
    } = useFetchData(getData, {
      current: 1,
      pageSize: 20,
      tableSize: 'middle', // 'default' | 'middle' | 'small'
      stripe: true,
      requestParams: {
        ...search_modelRef,
      },
    });
    watch(
      () => state.dataSource,
      () => {
        const total_ids: Array<number> = modalRef.selectedRowsTotal.map(item => item.id);
        state.dataSource.map((item: any) => {
          if (total_ids.indexOf(item.id) > -1) {
            modalRef.selectedRowsKey.push(item.id);
          }
        });
      },
    );
    const init = () => {
      let list: any[] = [];
      if (props.value instanceof Array) {
        if (props.value.length > 0) {
          list = props.value as Array<number>;
        }
      } else {
        if (props.value) {
          list.push(props.value);
        }
      }
      if (list.length > 0) {
        const tmp_ids: any[] = [];
        const tmp_item_ids: any[] = [];
        modalRef.datasource.map(item => tmp_item_ids.push(item.id));
        list.map(item => {
          if (modalRef.selectedRows.indexOf(Number(item)) == -1) {
            modalRef.selectedRows.push(Number(item));
            modalRef.selectedRowsKey.push(Number(item));
          }
          if (tmp_item_ids.indexOf(item) == -1) {
            tmp_ids.push(item);
          }
        });
        if (tmp_ids.length > 0) {
          getData({ current: 1, ...search_modelRef, pageSize: 2000, ids: tmp_ids.join(',') }).then(
            function (res: any) {
              res.data.forEach(item => {
                if (list.indexOf(item.id) >= 0) {
                  modalRef.datasource.push(item);
                  modalRef.selectedRowsTotal.push(item);
                }
              });
            },
          );
        }
      } else {
        modalRef.selectedRows.length = 0;
        modalRef.selectedRowsKey.length = 0;
        modalRef.selectedRowsTotal.length = 0;
      }
    };
    init();

    watch(
      () => props.value,
      () => {
        init();
      },
    );

    const handleTableChange = ({ current, pageSize }: any, filters: any, sorter: any) => {
      console.log(sorter);
      setPageInfo({
        current,
        pageSize,
        ...filters,
        ...search_modelRef,
      });

      reload();
    };

    const { validateInfos: searchValidateInfos } = useForm(search_modelRef, {});

    const search = () => {
      setPageInfo({
        current: 1,
        ...search_modelRef,
      });
      reload();
    };

    return {
      props,
      modalRef,
      datasource,
      state,
      loading,
      getDateTime,
      getDate,
      getFullDate,
      getTime,
      search,
      handleTableChange,
      rowSelection,
      searchValidateInfos,
      search_modelRef,
      searchConfig,
      searchConfigOptions,
      getData,
      columnState,
      dynamicColumns,
      slotList,
    };
  },

  data() {
    return {
      model: this.value,
    };
  },
});
</script>
